import { lazy } from 'react'
import { Route, Routes } from 'react-router'

import { AuthCallbackPage } from './features/auth/AuthCallbackPage'
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage'
import { SignInPage } from './features/auth/SignInPage'
import { SignUpPage } from './features/auth/SignUpPage'
import { AppShell } from './layout/AppShell'
import { RequireAuth, RequireWorkspace, RootRedirect } from './lib/auth/guards'
import { SystemStatusPage } from './pages/SystemStatusPage'

/**
 * Route tree only. No logic, no data fetching.
 *
 * Auth screens are eager: they are the landing surface and must paint fast.
 * Everything behind the shell is lazily loaded, so the initial bundle stays
 * within budget as the remaining 20 modules land.
 *
 * Reference: docs/04-frontend/04-routing-and-navigation.md
 */
const OnboardingPage = lazy(() =>
  import('./features/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
)
const WorkspacePickerPage = lazy(() =>
  import('./features/workspaces/WorkspacePickerPage').then((m) => ({
    default: m.WorkspacePickerPage,
  })),
)
const DashboardPage = lazy(() =>
  import('./features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const TeamPage = lazy(() =>
  import('./features/team/TeamPage').then((m) => ({ default: m.TeamPage })),
)
const ChatbotsPage = lazy(() =>
  import('./features/chatbots/ChatbotsPage').then((m) => ({ default: m.ChatbotsPage })),
)
const InboxPage = lazy(() =>
  import('./features/inbox/InboxPage').then((m) => ({ default: m.InboxPage })),
)
const VisitorsPage = lazy(() =>
  import('./features/visitors/VisitorsPage').then((m) => ({ default: m.VisitorsPage })),
)
const LeadsPage = lazy(() =>
  import('./features/leads/LeadsPage').then((m) => ({ default: m.LeadsPage })),
)
const AnalyticsPage = lazy(() =>
  import('./features/analytics/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
)
const PipelinePage = lazy(() =>
  import('./features/pipeline/PipelinePage').then((m) => ({ default: m.PipelinePage })),
)
const MyDayPage = lazy(() =>
  import('./features/tasks/MyDayPage').then((m) => ({ default: m.MyDayPage })),
)
const KnowledgePage = lazy(() =>
  import('./features/knowledge/KnowledgePage').then((m) => ({ default: m.KnowledgePage })),
)
const ChatbotDetailPage = lazy(() =>
  import('./features/chatbots/ChatbotDetailPage').then((m) => ({
    default: m.ChatbotDetailPage,
  })),
)
const AutomationsPage = lazy(() =>
  import('./features/automations/AutomationsPage').then((m) => ({
    default: m.AutomationsPage,
  })),
)
const TicketsPage = lazy(() =>
  import('./features/tickets/TicketsPage').then((m) => ({
    default: m.TicketsPage,
  })),
)
// Support tickets: one set of pages, mounted in the workspace, the reseller panel and Super Admin.
const SupportPage = lazy(() =>
  import('./features/support/SupportPage').then((m) => ({ default: m.SupportPage })),
)
const NewTicketPage = lazy(() =>
  import('./features/support/NewTicketPage').then((m) => ({ default: m.NewTicketPage })),
)
const SupportTicketPage = lazy(() =>
  import('./features/support/TicketPage').then((m) => ({ default: m.TicketPage })),
)
const TicketDeskPage = lazy(() =>
  import('./features/admin/views/TicketDeskPage').then((m) => ({ default: m.TicketDeskPage })),
)
const DepartmentsPage = lazy(() =>
  import('./features/team/departments/DepartmentsPage').then((m) => ({
    default: m.DepartmentsPage,
  })),
)
const IntegrationsPage = lazy(() =>
  import('./features/integrations/IntegrationsPage').then((m) => ({
    default: m.IntegrationsPage,
  })),
)
const DevelopersPage = lazy(() =>
  import('./features/developers/DevelopersPage').then((m) => ({
    default: m.DevelopersPage,
  })),
)
const ProductsPage = lazy(() =>
  import('./features/products/ProductsPage').then((m) => ({
    default: m.ProductsPage,
  })),
)
const OrdersPage = lazy(() =>
  import('./features/commerce/OrdersPage').then((m) => ({
    default: m.OrdersPage,
  })),
)
const BookingsPage = lazy(() =>
  import('./features/bookings/BookingsPage').then((m) => ({
    default: m.BookingsPage,
  })),
)
const BillingPage = lazy(() =>
  import('./features/billing/BillingPage').then((m) => ({
    default: m.BillingPage,
  })),
)

// Platform administration. Lazy and separate from the workspace tree: almost
// nobody loads it, and it must never share a chunk with customer screens.
const RequirePlatformAdmin = lazy(() =>
  import('./features/admin/guard').then((m) => ({ default: m.RequirePlatformAdmin })),
)
const SuperAdminPage = lazy(() =>
  import('./features/admin/SuperAdminPage').then((m) => ({ default: m.SuperAdminPage })),
)
const AiConfigPage = lazy(() =>
  import('./features/admin/AiConfigPage').then((m) => ({ default: m.AiConfigPage })),
)
const NewResellerPage = lazy(() =>
  import('./features/admin/views/NewResellerPage').then((m) => ({ default: m.NewResellerPage })),
)
const ResellerDetailPage = lazy(() =>
  import('./features/admin/views/ResellerDetailPage').then((m) => ({
    default: m.ResellerDetailPage,
  })),
)

// The reseller panel: not workspace-scoped, its own chunk.
const RequireReseller = lazy(() =>
  import('./features/reseller/guard').then((m) => ({ default: m.RequireReseller })),
)
const ResellerPanelPage = lazy(() =>
  import('./features/reseller/ResellerPanelPage').then((m) => ({
    default: m.ResellerPanelPage,
  })),
)
const NewClientPage = lazy(() =>
  import('./features/reseller/NewClientPage').then((m) => ({ default: m.NewClientPage })),
)
const ClientDetailPage = lazy(() =>
  import('./features/reseller/ClientDetailPage').then((m) => ({
    default: m.ClientDetailPage,
  })),
)
// Public: an invitation is opened before the person may even have an account.
const InviteAcceptPage = lazy(() =>
  import('./features/invites/InviteAcceptPage').then((m) => ({ default: m.InviteAcceptPage })),
)

export function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/auth/signin" element={<SignInPage />} />
      <Route path="/auth/signup" element={<SignUpPage />} />
      <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/status" element={<SystemStatusPage />} />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />

      {/* Authenticated */}
      <Route element={<RequireAuth />}>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/workspaces" element={<WorkspacePickerPage />} />

        <Route path="/w/:workspaceSlug" element={<RequireWorkspace />}>
          <Route element={<AppShell />}>
            <Route index element={<DashboardPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="bots" element={<ChatbotsPage />} />
            <Route path="bots/:botId" element={<ChatbotDetailPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="visitors" element={<VisitorsPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="pipeline" element={<PipelinePage />} />
            <Route path="my-day" element={<MyDayPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="knowledge" element={<KnowledgePage />} />
            <Route path="automations" element={<AutomationsPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="support/new" element={<NewTicketPage />} />
            <Route path="support/:ticketId" element={<SupportTicketPage />} />
            <Route path="team/departments" element={<DepartmentsPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="developers" element={<DevelopersPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="billing" element={<BillingPage />} />
          </Route>
        </Route>

        {/* Platform administration — NOT workspace-scoped. */}
        <Route path="/admin" element={<RequirePlatformAdmin />}>
          <Route element={<AppShell />}>
            <Route index element={<SuperAdminPage />} />
            <Route path="ai" element={<AiConfigPage />} />
            <Route path="resellers/new" element={<NewResellerPage />} />
            <Route path="resellers/:resellerId" element={<ResellerDetailPage />} />
            <Route path="support/:ticketId" element={<TicketDeskPage />} />
            <Route path="*" element={<SuperAdminPage />} />
          </Route>
        </Route>

        {/* Reseller panel — NOT workspace-scoped. */}
        <Route path="/reseller" element={<RequireReseller />}>
          <Route element={<AppShell />}>
            <Route index element={<ResellerPanelPage />} />
            <Route path="clients/new" element={<NewClientPage />} />
            <Route path="clients/:clientId" element={<ClientDetailPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="support/new" element={<NewTicketPage />} />
            <Route path="support/:ticketId" element={<SupportTicketPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<RootRedirect />} />
    </Routes>
  )
}
